import { useEffect, useState } from 'react';
import { parse } from 'graphql';

import { createAuthenticatedGraphQLClient } from '../auth/createAuthenticatedGraphQLClient';
import { resolveClientEnv } from '../graphql/config/resolveClientEnv';
import type { DoorVisitListQuery, DoorVisitListQueryVariables } from '../graphql/queries/doorVisitList.generated';
import {
  buildDoorVisitNormalizedQueryVariables,
  DEFAULT_FILTERS,
  isDoorVisitCustomDateRangeValid,
  type DoorVisitFilterState,
} from './filters/doorVisitFilters';

export type DoorVisitListItem = DoorVisitListQuery['doorVisitList']['items'][number];

type FetchImpl = Parameters<typeof createAuthenticatedGraphQLClient>[0]['fetchImpl'];

export type UseDoorVisitListOptions = {
  fetchImpl?: FetchImpl;
  filters?: DoorVisitFilterState;
  enabled?: boolean;
};

export type UseDoorVisitListResult = {
  items: DoorVisitListItem[];
  total: number;
  loading: boolean;
  error: string | null;
  calendarTimeZone: string | null;
  page: number;
  rowsPerPage: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  refetch: () => void;
};

export const DOOR_VISIT_LIST_DOCUMENT = parse(/* GraphQL */ `
  query DoorVisitList($first: Int, $offset: Int, $filter: DoorVisitListFilter, $sort: DoorVisitListSort) {
    doorVisitList(first: $first, offset: $offset, filter: $filter, sort: $sort) {
      total
      calendarTimeZone
      items {
        doorVisitId
        sessionId
        dwellStartedAt
        dwellEndedAt
        canvasserId
        address
        foHouseholdId
        matchedHouseholdId
        visitStatus
        isPartial
        coreLeadId
        coreCallId
        hasClip
        hasSummary
        conversationSummary
        voterName
      }
    }
  }
`);

export const useDoorVisitList = ({
  fetchImpl,
  filters = DEFAULT_FILTERS,
  enabled = true,
}: UseDoorVisitListOptions = {}): UseDoorVisitListResult => {
  const [items, setItems] = useState<DoorVisitListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calendarTimeZone, setCalendarTimeZone] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [refetchCounter, setRefetchCounter] = useState(0);

  // Identity-stable key for the filter/sort combination — an object literal
  // would change reference every render and re-trigger the effect on a loop,
  // the same hazard `fetchImpl` already guards against below.
  const filterKey = JSON.stringify([
    filters.dateRange,
    filters.canvasserId,
    filters.customSlotName,
    filters.customSlotValue,
    filters.fromDate,
    filters.throughDate,
    filters.sort,
  ]);
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) {
    // Reset during render (not in an effect) so a filter change can never
    // fire one request at the stale offset before the reset takes effect.
    setLastFilterKey(filterKey);
    if (page !== 0) setPage(0);
  }

  useEffect(() => {
    let cancelled = false;

    const run = async (): Promise<void> => {
      if (
        !enabled ||
        (filters.dateRange === 'custom' && !isDoorVisitCustomDateRangeValid(filters.fromDate, filters.throughDate))
      ) {
        setItems([]);
        setTotal(0);
        setError(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      try {
        const env = resolveClientEnv();
        const client = createAuthenticatedGraphQLClient({ env, fetchImpl });

        const variables: DoorVisitListQueryVariables = {
          first: rowsPerPage,
          offset: page * rowsPerPage,
          ...buildDoorVisitNormalizedQueryVariables(filters),
        };

        const data = await client.request<DoorVisitListQuery>({
          document: DOOR_VISIT_LIST_DOCUMENT,
          variables,
        });

        if (!cancelled) {
          setItems(data.doorVisitList.items);
          setTotal(data.doorVisitList.total);
          setCalendarTimeZone(data.doorVisitList.calendarTimeZone);

          // If the current page landed past the end of the (possibly just-shrunk)
          // result set, snap back to the last valid page so the user never gets
          // stuck on a page with total > 0 but zero items and no way to navigate
          // back (the table/footer only render when items.length > 0).
          const lastPage = Math.max(0, Math.ceil(data.doorVisitList.total / rowsPerPage) - 1);
          if (data.doorVisitList.total > 0 && page > lastPage) {
            setPage(lastPage);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load door visits.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [page, rowsPerPage, refetchCounter, fetchImpl, filterKey, enabled, filters.dateRange, filters.fromDate, filters.throughDate]);

  const handleSetRowsPerPage = (newRowsPerPage: number): void => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const refetch = (): void => {
    setRefetchCounter(c => c + 1);
  };

  return {
    items,
    total,
    loading,
    error,
    calendarTimeZone,
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage: handleSetRowsPerPage,
    refetch,
  };
};
